import { useEffect, useState } from 'react';
import { ActionIcon, Anchor, Avatar, Box, Button, Center, Group, Loader, Stack, Text, Tooltip } from '@mantine/core';
import { IconArrowUpRight, IconCheck, IconCopy, IconLogout } from '@tabler/icons-react';
import { useAuth } from '@workos-inc/authkit-react';
import { SectionTitle } from './TitleSection';
import { BadgeBlock } from '../../components/BadgeBlock';
import { InlineNotice } from '../../components/InlineNotice';
import { ProductIcon } from '../../components/ProductIcon';
import { StatusBadge } from '../../components/StatusBadge';
import { productCatalogue, type ProductCatalogueEntry } from '../../catalogue/products';
import { fetchMe, MeFetchError } from '../../api/me';
import type { EntitlementMap, ProductEntitlement } from '../../types/entitlements';

const pageData = {
  title: 'My account',
  subtitle: 'Your Cloudhouse identity, product access, and session.',
};

const DAY_MS = 86_400_000;

const CARD_STYLE = {
  border: '1px solid var(--border-default)',
  borderRadius: 12,
  background: 'var(--bg-surface)',
  overflow: 'hidden',
};

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Whole days until `iso`, floored at 0. Callers pass a date `formatDate` already accepted. */
function daysUntil(iso: string, now: number = Date.now()): number {
  return Math.max(0, Math.ceil((new Date(iso).getTime() - now) / DAY_MS));
}

function trialDetail(entitlement: ProductEntitlement): string {
  const endsAt = entitlement.trialEndsAt;
  const ends = formatDate(endsAt);
  if (!endsAt || ends === '—') return 'Trial';
  const left = daysUntil(endsAt);
  return `Trial · ends ${ends} (${left} ${left === 1 ? 'day' : 'days'} left)`;
}

function accessDetail(entitlement: ProductEntitlement | undefined): string {
  if (entitlement?.status !== 'active') {
    return entitlement?.status === 'available'
      ? 'Available to your organisation'
      : 'Not available to your organisation';
  }
  if (entitlement.plan === 'trial') return trialDetail(entitlement);
  const renews = formatDate(entitlement.renewalDate);
  return entitlement.renewalDate && renews !== '—' ? `Renews ${renews}` : 'Active';
}

function statusVariant(entitlement: ProductEntitlement | undefined): 'active' | 'available' | 'none' {
  if (entitlement?.status === 'active') return 'active';
  return entitlement?.status === 'available' ? 'available' : 'none';
}

function CopyValueButton({ label, value }: Readonly<{ label: string; value: string }>) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      // Insecure context or a denied permission: say nothing rather than claim a copy that did not happen.
    }
  };

  return (
    <Tooltip label={copied ? 'Copied' : label}>
      <ActionIcon
        variant="subtle"
        size="sm"
        aria-label={label}
        onClick={copy}
        style={{
          '--ai-color': 'var(--text-subtle)',
          '--ai-hover-color': 'var(--accent-text)',
          '--ai-hover': 'var(--bg-subtle)',
        }}
      >
        {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
      </ActionIcon>
    </Tooltip>
  );
}

function DetailRow({ label, value, copyLabel }: Readonly<{ label: string; value: string; copyLabel?: string }>) {
  return (
    <Group
      justify="space-between"
      wrap="nowrap"
      gap={16}
      p="12px 18px"
      style={{ borderTop: '1px solid var(--border-default)' }}
    >
      <Text fz={11} fw={700} tt="uppercase" lts={0.5} c="var(--text-subtle)">{label}</Text>
      <Group gap={6} wrap="nowrap">
        <Text fz="sm" c="var(--text-default)" style={{ wordBreak: 'break-all' }}>{value}</Text>
        {copyLabel && <CopyValueButton label={copyLabel} value={value} />}
      </Group>
    </Group>
  );
}

function IdentityCard() {
  const auth = useAuth();
  const user = auth.user;
  const email = user?.email ?? '';
  const firstName = user?.firstName ?? '';
  const lastName = user?.lastName ?? '';
  const initials = `${firstName[0] ?? ''}${lastName[0] ?? ''}`.toUpperCase() || email[0]?.toUpperCase() || 'U';
  const fullName = [firstName, lastName].filter(Boolean).join(' ') || email || 'Your account';

  return (
    <Box mt={22} style={CARD_STYLE} data-testid="profile-me-identity">
      <Group gap={16} wrap="nowrap" p="18px 18px 20px">
        <Avatar
          size={56}
          radius="xl"
          src={user?.profilePictureUrl ?? undefined}
          alt={fullName}
          style={{ '--avatar-bg': 'linear-gradient(135deg,#516470,#0D2839)', '--avatar-bd': 'none' }}
        >
          <Text fz="md" fw={700} c="white">{initials}</Text>
        </Avatar>
        <Stack gap={6} flex={1}>
          <Text fz="h4" fw={700} lh="normal" c="var(--text-default)">{fullName}</Text>
          <Group gap={8} align="center">
            {email && <Text fz="sm" c="var(--text-muted)" lh="normal">{email}</Text>}
            <BadgeBlock
              bdrs="xl"
              fz={11}
              color={user?.emailVerified ? 'var(--status-success)' : 'var(--status-warning)'}
            >
              {user?.emailVerified ? 'Verified' : 'Unverified'}
            </BadgeBlock>
            {auth.role && <BadgeBlock bdrs="xl" fz={11} color="var(--accent-text)">{auth.role}</BadgeBlock>}
          </Group>
        </Stack>
      </Group>

      <DetailRow label="Member since" value={formatDate(user?.createdAt)} />
      <DetailRow label="Last sign-in" value={formatDate(user?.lastSignInAt)} />
      <DetailRow label="Profile updated" value={formatDate(user?.updatedAt)} />
      <DetailRow label="User ID" value={user?.id ?? '—'} copyLabel={user?.id ? 'Copy user ID' : undefined} />
      <DetailRow
        label="Organisation ID"
        value={auth.organizationId ?? 'No organisation'}
        copyLabel={auth.organizationId ? 'Copy organisation ID' : undefined}
      />
    </Box>
  );
}

function AccessRow({ product, entitlement, first }: Readonly<{
  product: ProductCatalogueEntry;
  entitlement: ProductEntitlement | undefined;
  first: boolean;
}>) {
  const isActive = entitlement?.status === 'active';
  return (
    <Group
      wrap="nowrap"
      gap={14}
      p="14px 18px"
      data-testid={`profile-me-access-${product.key}`}
      style={first ? undefined : { borderTop: '1px solid var(--border-default)' }}
    >
      <ProductIcon productKey={product.key} accent={product.brandColour} size={38} muted={!isActive} />
      <Stack gap={4} flex={1}>
        <Group gap={10} align="center" wrap="nowrap">
          <Text fz="sm" fw={600} lh="normal" c="var(--text-default)">{product.name}</Text>
          <StatusBadge variant={statusVariant(entitlement)} />
        </Group>
        <Group gap={10} align="center" wrap="wrap">
          <Text fz="xs" c="var(--text-muted)" lh="normal">{accessDetail(entitlement)}</Text>
          {!isActive && entitlement?.trialAvailable && (
            <Text fz="xs" c="var(--text-subtle)" lh="normal">Trial available</Text>
          )}
        </Group>
      </Stack>
      {isActive && product.path && (
        <Anchor
          href={product.path}
          target="_blank"
          rel="noopener noreferrer"
          fz="sm"
          fw={600}
          c="var(--accent-text)"
          aria-label={`Launch ${product.name}`}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }}
        >
          Launch
          <IconArrowUpRight size={14} stroke={2.2} />
        </Anchor>
      )}
    </Group>
  );
}

type AccessState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'ready'; products: EntitlementMap };

function useEntitlements(): AccessState {
  const auth = useAuth();
  const [state, setState] = useState<AccessState>({ kind: 'loading' });

  useEffect(() => {
    const ctrl = new AbortController();
    void (async () => {
      try {
        const token = await auth.getAccessToken();
        if (!token) {
          setState({ kind: 'error' });
          return;
        }
        const { products } = await fetchMe(token, ctrl.signal);
        setState({ kind: 'ready', products });
      } catch (e) {
        if (e instanceof MeFetchError || (e as Error).name !== 'AbortError') setState({ kind: 'error' });
      }
    })();
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once on mount
  }, []);

  return state;
}

function AccessCard() {
  const state = useEntitlements();

  if (state.kind === 'loading') {
    return <Center py={40}><Loader size="sm" /></Center>;
  }
  if (state.kind === 'error') {
    return (
      <InlineNotice
        tone="error"
        message="Couldn't load your product access — check the entitlements API is running."
      />
    );
  }

  return (
    <Box mt={14} style={CARD_STYLE} data-testid="profile-me-access">
      {productCatalogue.filter((p) => !p.notYetOffered).map((product, index) => (
        <AccessRow
          key={product.key}
          product={product}
          entitlement={state.products[product.key]}
          first={index === 0}
        />
      ))}
    </Box>
  );
}

function SectionLabel({ children }: Readonly<{ children: string }>) {
  return (
    <Text mt={30} lts={0.5} fz="xs" fw={700} tt="uppercase" lh="normal" c="var(--text-subtle)">
      {children}
    </Text>
  );
}

export function ProfileMe() {
  const auth = useAuth();
  return (
    <Stack flex={1} style={{ overflowY: 'auto' }} px={32} py={24} data-testid="profile-me-tab" gap={0}>
      <SectionTitle {...pageData} />
      <IdentityCard />

      <SectionLabel>Your access</SectionLabel>
      <AccessCard />

      <SectionLabel>Session</SectionLabel>
      <Group mt={14}>
        <Button
          leftSection={<IconLogout size={16} />}
          onClick={() => auth.signOut()}
          variant="light"
          style={{
            '--button-bg': 'var(--button-destructive-subtle-background-default)',
            '--button-hover': 'var(--button-destructive-subtle-background-hover)',
            '--button-color': 'var(--status-danger)',
          }}
        >
          Sign out
        </Button>
      </Group>
    </Stack>
  );
}
